import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Lock, ShieldCheck, ShieldAlert, RefreshCw, Play } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { API } from "../../App";

const EncryptionTab = () => {
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState("");
  const load = async () => {
    setBusy("load");
    try { setStatus((await axios.get(`${API}/admin/encryption/status`)).data); }
    catch (e) { toast.error(e.response?.data?.detail || "Impossible de lire l'état du chiffrement"); }
    finally { setBusy(""); }
  };
  useEffect(() => { load(); }, []);

  const migrate = async () => {
    if (!window.confirm("Chiffrer maintenant tous les documents cliniques encore en clair ? L'opération est irréversible sans la clé et peut prendre quelques minutes.")) return;
    setBusy("migrate");
    try {
      const res = (await axios.post(`${API}/admin/encryption/migrate`)).data;
      toast.success(`${res.totals.updated} document(s) chiffré(s)`);
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || "Échec de la migration"); }
    finally { setBusy(""); }
  };

  const t = status?.totals;
  const allDone = t && t.plain === 0;
  return (
    <div className="space-y-6" data-testid="admin-encryption-tab">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg"><Lock className="w-5 h-5 text-primary" /> Chiffrement du contenu clinique (AES-256-GCM)</CardTitle>
          <p className="text-sm text-muted-foreground">Comptes rendus, transcriptions, e-mails de suivi, analyses, anamnèses, conceptualisations, synthèses et saisies patient sont chiffrés au repos, document par document.</p>
        </CardHeader>
        <CardContent className="space-y-4">
          {!status ? <p className="text-sm text-muted-foreground">Chargement…</p> : (
            <>
              <div className="flex flex-wrap items-center gap-3">
                <span className={`inline-flex items-center gap-1.5 text-sm px-3 py-1 rounded-full ${status.enabled ? "bg-emerald-100 text-emerald-800 dark:bg-emerald-950 dark:text-emerald-200" : "bg-red-100 text-red-800 dark:bg-red-950 dark:text-red-200"}`} data-testid="encryption-key-status">
                  {status.enabled ? <><ShieldCheck className="w-4 h-4" /> Clé active sur ce serveur</> : <><ShieldAlert className="w-4 h-4" /> Clé absente : CLINICAL_ENCRYPTION_KEY manquante</>}
                </span>
                <span className="text-sm" data-testid="encryption-totals">
                  <strong>{t.encrypted}</strong> document(s) chiffré(s) · <strong className={t.plain ? "text-amber-700" : ""}>{t.plain}</strong> encore en clair
                </span>
              </div>
              {!status.enabled && <p className="text-xs text-red-700 bg-red-50 dark:bg-red-950/30 rounded-lg p-2">Ajoutez la variable d'environnement <code>CLINICAL_ENCRYPTION_KEY</code> au déploiement puis redéployez avant de lancer la migration.</p>}
              {allDone && status.enabled && <p className="text-xs text-emerald-700 bg-emerald-50 dark:bg-emerald-950/30 rounded-lg p-2" data-testid="encryption-all-done">Tout le contenu clinique est chiffré. Relancez la vérification après une restauration de sauvegarde ou un import de données.</p>}
              <div className="flex flex-wrap gap-2">
                <Button variant="outline" size="sm" onClick={load} disabled={busy !== ""} className="gap-1.5" data-testid="encryption-refresh-btn"><RefreshCw className={`w-4 h-4 ${busy === "load" ? "animate-spin" : ""}`} /> Vérifier</Button>
                <Button size="sm" onClick={migrate} disabled={busy !== "" || !status.enabled || allDone} className="gap-1.5" data-testid="encryption-migrate-btn"><Play className="w-4 h-4" /> {busy === "migrate" ? "Chiffrement en cours…" : `Chiffrer les ${t.plain} document(s) en clair`}</Button>
              </div>
              {status.collections.length > 0 && (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm" data-testid="encryption-table">
                    <thead><tr className="border-b text-left text-muted-foreground"><th className="py-2 pr-4">Collection</th><th className="py-2 pr-4 text-right">En clair</th><th className="py-2 pr-4 text-right">Chiffrés</th></tr></thead>
                    <tbody>
                      {status.collections.map((c) => (
                        <tr key={c.collection} className="border-b last:border-0" data-testid={`encryption-row-${c.collection}`}>
                          <td className="py-1.5 pr-4 font-mono text-xs">{c.collection}</td>
                          <td className={`py-1.5 pr-4 text-right ${c.plain ? "text-amber-700 font-medium" : "text-muted-foreground"}`}>{c.plain}</td>
                          <td className="py-1.5 pr-4 text-right">{c.encrypted}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default EncryptionTab;
